import { useEffect, useState, type FormEvent } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSpinner, faTriangleExclamation } from '@fortawesome/free-solid-svg-icons';
import { Modal } from '@/components/shared/Modal';
import type {
  PlatformUserSummary,
  UpdatePlatformUserIdentityRequest,
} from '@/types/platform-user.types';

/**
 * Phase 12.6-5 — identity edit (`PATCH /platform/users/:id`).
 *
 * Design of record: `PLATFORM_USERS_DESIGN.md` v1.0 §4.7, §6. Route delivered by
 * 12.6-1 and verified over real HTTP in 12.6-2.
 *
 * ── Three fields, and that is load-bearing ──────────────────────────────────
 * `displayName`, `email`, `username` — nothing else. `role` and `isActive` keep
 * their own routes, which carry the self-mutation guards, and the backend DTO
 * omits them so `forbidNonWhitelisted` rejects them with a **400** rather than
 * ignoring them. **There is no password field anywhere in this phase.**
 *
 * ── Only an email change revokes sessions ───────────────────────────────────
 * §4.7 rule 2: the email IS the platform sign-in identifier (verified in 12.6-1
 * across all nine `platformUser` lookups — login resolves by email alone).
 * Changing it bumps `tokenVersion` and revokes every refresh token. Changing
 * `displayName` or `username` revokes **nothing**, and this dialog must not
 * imply otherwise.
 *
 * The warning is driven by the SAME normalisation the server applies —
 * `trim().toLowerCase()` compared against the stored value — so re-typing the
 * same address in different case correctly warns about nothing.
 *
 * ── Only changed fields are sent ────────────────────────────────────────────
 * An unchanged field is omitted rather than sent unchanged. The server treats a
 * no-op as a no-op either way, but sending the current email back would make the
 * request indistinguishable from a real change in the audit trail's eyes.
 *
 * ── Typed state survives a rejected submit ──────────────────────────────────
 * `submitting` and `error` are PROPS. A 409 on a duplicate email leaves every
 * typed value exactly where it was, so the operator corrects one field instead
 * of retyping three.
 */

interface Props {
  open: boolean;
  user: PlatformUserSummary;
  /** True when editing your own account — an email change signs YOU out. */
  isSelf: boolean;
  submitting: boolean;
  error: string;
  onClose: () => void;
  onSubmit: (payload: UpdatePlatformUserIdentityRequest) => void;
}

/** Mirrors the server's normalisation, so the UI and the backend agree on "changed". */
const normalizeEmail = (value: string): string => value.trim().toLowerCase();

export function PlatformUserIdentityDialog({
  open,
  user,
  isSelf,
  submitting,
  error,
  onClose,
  onSubmit,
}: Props) {
  const [displayName, setDisplayName] = useState('');
  const [email, setEmail] = useState('');
  const [username, setUsername] = useState('');

  // Reset from the row whenever the dialog opens for a (possibly different)
  // user. Not on every render — that would fight the operator's typing.
  useEffect(() => {
    if (!open) return;
    setDisplayName(user.displayName);
    setEmail(user.email);
    setUsername(user.username ?? '');
  }, [open, user.id, user.displayName, user.email, user.username]);

  if (!open) return null;

  const nextDisplayName = displayName.trim();
  const nextEmail = normalizeEmail(email);
  const nextUsername = username.trim();
  const currentUsername = user.username ?? '';

  const displayNameChanged = nextDisplayName !== user.displayName;
  const emailChanged = nextEmail !== user.email;
  const usernameChanged = nextUsername !== currentUsername;

  const anyChange = displayNameChanged || emailChanged || usernameChanged;
  const displayNameValid = nextDisplayName.length > 0;
  const canSubmit = !submitting && anyChange && displayNameValid;

  const submit = (e: FormEvent): void => {
    e.preventDefault();
    if (!canSubmit) return;

    // Only what actually changed.
    const payload: UpdatePlatformUserIdentityRequest = {};
    if (displayNameChanged) payload.displayName = nextDisplayName;
    if (emailChanged) payload.email = nextEmail;
    if (usernameChanged) {
      // Explicit `null` clears it; the backend distinguishes null from absent,
      // so an emptied field must not be sent as an empty string.
      payload.username = nextUsername === '' ? null : nextUsername;
    }
    onSubmit(payload);
  };

  return (
    <Modal open onClose={submitting ? () => undefined : onClose} title="Edit identity" size="md">
      <form onSubmit={submit} className="space-y-4 px-6 py-5">
        <div className="rounded-lg border border-border bg-muted/30 px-3 py-2">
          <p className="text-xs text-muted-foreground">Editing</p>
          <p className="break-all text-sm font-medium text-foreground">{user.email}</p>
        </div>

        <div>
          <label
            htmlFor="platform-user-display-name"
            className="mb-1.5 block text-sm font-medium text-foreground"
          >
            Display name <span className="text-destructive">*</span>
          </label>
          <input
            id="platform-user-display-name"
            type="text"
            value={displayName}
            maxLength={255}
            autoComplete="off"
            onChange={(e) => setDisplayName(e.target.value)}
            className="h-11 w-full rounded-lg border border-border bg-background px-3 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring sm:h-10"
          />
          {!displayNameValid && (
            <p className="mt-1.5 text-xs text-destructive">A display name is required.</p>
          )}
        </div>

        <div>
          <label
            htmlFor="platform-user-email"
            className="mb-1.5 block text-sm font-medium text-foreground"
          >
            Email <span className="text-destructive">*</span>
          </label>
          <input
            id="platform-user-email"
            type="email"
            value={email}
            maxLength={255}
            autoComplete="off"
            onChange={(e) => setEmail(e.target.value)}
            className="h-11 w-full rounded-lg border border-border bg-background px-3 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring sm:h-10"
          />
        </div>

        <div>
          <label
            htmlFor="platform-user-username"
            className="mb-1.5 block text-sm font-medium text-foreground"
          >
            Username <span className="text-muted-foreground">(optional)</span>
          </label>
          <input
            id="platform-user-username"
            type="text"
            value={username}
            maxLength={100}
            autoComplete="off"
            onChange={(e) => setUsername(e.target.value)}
            placeholder="Leave blank to clear"
            className="h-11 w-full rounded-lg border border-border bg-background px-3 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring sm:h-10"
          />
          <p className="mt-1.5 text-xs text-muted-foreground">
            Not a sign-in identifier on the platform — changing or clearing it does not affect
            anyone’s session.
          </p>
        </div>

        {/* ── The email consequence, shown BEFORE confirmation (§6) ─────────
            Only when the email actually changes, and only ever about the email.
            A name-only edit deliberately shows nothing here. */}
        {emailChanged && (
          <div
            role="note"
            className="flex items-start gap-2 rounded-lg border border-amber-500/30 bg-amber-500/5 px-3 py-2"
          >
            <FontAwesomeIcon
              icon={faTriangleExclamation}
              className="mt-0.5 h-3.5 w-3.5 shrink-0 text-amber-600 dark:text-amber-400"
            />
            <p className="text-sm text-amber-700 dark:text-amber-400">
              {isSelf ? (
                <>
                  Email is the platform sign-in identifier, so changing it ends every session on
                  this account — <strong>including yours. You will be signed out.</strong>
                </>
              ) : (
                <>
                  Email is the platform sign-in identifier, so changing it{' '}
                  <strong>ends this account’s active sessions immediately</strong>. They will sign
                  in with the new address.
                </>
              )}
            </p>
          </div>
        )}

        {error && (
          <div
            role="alert"
            className="flex items-start gap-2 rounded-lg border border-destructive/30 bg-destructive/5 px-3 py-2"
          >
            <FontAwesomeIcon
              icon={faTriangleExclamation}
              className="mt-0.5 h-3.5 w-3.5 shrink-0 text-destructive"
            />
            <p className="text-sm text-destructive">{error}</p>
          </div>
        )}

        <div className="flex flex-col-reverse gap-2 pt-1 sm:flex-row sm:justify-end">
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            className="min-h-11 rounded-lg border border-border px-4 py-2 text-sm font-medium text-foreground transition-colors hover:bg-muted disabled:opacity-50 sm:min-h-0"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={!canSubmit}
            className="min-h-11 rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground transition-colors hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-50 sm:min-h-0"
          >
            {submitting && <FontAwesomeIcon icon={faSpinner} spin className="mr-2 h-3.5 w-3.5" />}
            Save changes
          </button>
        </div>
      </form>
    </Modal>
  );
}
